import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { adminPanelAPI } from '../../services/api';
import { SolarPanel } from '../../types';
import { Alert } from '../common/Alert';
import { Input } from '../common/Input';
import { Button } from '../common/Button';

interface EditPanelModalProps {
  panel: SolarPanel;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedPanel: SolarPanel) => void;
}

type PanelFormData = Omit<SolarPanel, 'id' | 'createdAt' | 'updatedAt' | 'inventory'>;

export const EditPanelModal: React.FC<EditPanelModalProps> = ({
  panel,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { register, handleSubmit, formState: { errors }, reset } = useForm<PanelFormData>({
    defaultValues: panel
  });

  useEffect(() => {
    if (isOpen && panel) {
      reset(panel);
    }
  }, [isOpen, panel, reset]);

  const onSubmit = async (data: PanelFormData) => {
    setLoading(true);
    setError('');

    try {
      const updatedPanel = await adminPanelAPI.updatePanel(panel.id!, data);
      onSuccess(updatedPanel);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update panel');
    } finally {
      setLoading(false);
    }
  };

  const inputSections = [
    {
      title: 'Basic Information',
      fields: [
        { name: 'SKU', label: 'SKU', type: 'text', required: true },
        { name: 'Description', label: 'Description', type: 'text', required: true },
        { name: 'CellType', label: 'Cell Type', type: 'text', required: true },
        { name: 'ConnectorType', label: 'Connector Type', type: 'text', required: true }
      ]
    },
    {
      title: 'Power Specifications',
      fields: [
        { name: 'Watts_W', label: 'Power (W)', type: 'number', required: true },
        { name: 'Pmax_W', label: 'Pmax (W)', type: 'number', required: true },
        { name: 'Vmp_V', label: 'Vmp (V)', type: 'number', step: '0.01', required: true },
        { name: 'Imp_A', label: 'Imp (A)', type: 'number', step: '0.01', required: true },
        { name: 'Voc_V', label: 'Voc (V)', type: 'number', step: '0.01', required: true },
        { name: 'Isc_A', label: 'Isc (A)', type: 'number', step: '0.01', required: true }
      ]
    },
    {
      title: 'Physical Dimensions',
      fields: [
        { name: 'Length_mm', label: 'Length (mm)', type: 'number', required: true },
        { name: 'Width_mm', label: 'Width (mm)', type: 'number', required: true },
        { name: 'Thickness_mm', label: 'Thickness (mm)', type: 'number', required: true },
        { name: 'Weight_kg', label: 'Weight (kg)', type: 'number', step: '0.1', required: true },
        { name: 'NumberOfCells_count', label: 'Number of Cells', type: 'number', required: true },
        { name: 'CableLength_mm', label: 'Cable Length (mm)', type: 'number', required: true }
      ]
    },
    {
      title: 'Performance & Logistics',
      fields: [
        { name: 'ModuleEfficiency_percent', label: 'Module Efficiency (%)', type: 'number', step: '0.01', required: true },
        { name: 'TempCoeffPmax_percent_per_C', label: 'Temp Coeff Pmax (%/°C)', type: 'number', step: '0.01', required: true },
        { name: 'TempCoeffVoc_percent_per_C', label: 'Temp Coeff Voc (%/°C)', type: 'number', step: '0.01', required: true },
        { name: 'TempCoeffIsc_percent_per_C', label: 'Temp Coeff Isc (%/°C)', type: 'number', step: '0.01', required: true },
        { name: 'PanelsPerPallet_count', label: 'Panels per Pallet', type: 'number', required: true },
        { name: 'PalletsPerTruck_count', label: 'Pallets per Truck', type: 'number', required: true },
        { name: 'SnowLoad_Pa', label: 'Snow Load (Pa)', type: 'number', required: true },
        { name: 'WindLoad_Pa', label: 'Wind Load (Pa)', type: 'number', required: true }
      ]
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Edit Panel: {panel.SKU}</h2>
            <Button variant="secondary" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6">
          {error && (
            <Alert
              type="error"
              message={error}
              onClose={() => setError('')}
            />
          )}

          <div className="space-y-8">
            {inputSections.map((section, sectionIndex) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: sectionIndex * 0.1 }}
                className="bg-gray-50 p-6 rounded-lg"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{section.title}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {section.fields.map((field) => (
                    <Input
                      key={field.name}
                      label={field.label}
                      type={field.type}
                      step={field.step}
                      {...register(field.name as keyof PanelFormData, {
                        required: field.required ? `${field.label} is required` : false,
                        valueAsNumber: field.type === 'number'
                      })}
                      error={errors[field.name as keyof PanelFormData]?.message}
                    />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              className="flex items-center"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
